import type { CalendarEvent } from '../api/types';

/*
    The arithmetic a month grid is made of.

    Its own module, and pure, for the same reason the pane breakpoints are: these are decisions with
    right and wrong answers — which day a Sunday event belongs to, whether an all-day event on the
    12th touches the 13th — and they can be asked directly by a test instead of being looked for in
    a rendered grid.

    Everything here works in the reader's own timezone. An instant from the server is drawn on the
    day it happens where the person is sitting, which is the only day that means anything to them;
    an all-day event is a date and is never converted at all.
*/

/** One cell. `key` is the day's own name — `2026-09-12` — and is what everything is grouped by. */
export interface Day {
    key: string;
    date: Date;
    /** False for the days either side that fill the first and last week. */
    inMonth: boolean;
    today: boolean;
}

/** The day a Date falls on, named the way the server names an all-day event. */
export function dayKey(d: Date): string {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}

/** A day key back into a local Date at midnight. Never `new Date('2026-09-12')`, which is UTC. */
export function fromDayKey(key: string): Date {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/*
    Six weeks, always.

    A month needs four to six rows depending on where it starts, and a grid that changes height
    every month makes the whole screen jump on every click. Six is the maximum, so six it is —
    the extra row is the neighbouring days, drawn faintly, which are useful anyway.

    The week starts on Monday: this is a Turkish product, and every wall calendar here does.
*/
export function monthGrid(year: number, month: number, today = new Date()): Day[] {
    const first = new Date(year, month, 1);
    // getDay() is 0 for Sunday; Monday-first means Sunday is the seventh column, not the first.
    const lead = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - lead);

    const todayKey = dayKey(today);
    const out: Day[] = [];
    for (let i = 0; i < 42; i++) {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const key = dayKey(date);
        out.push({ key, date, inMonth: date.getMonth() === month, today: key === todayKey });
    }
    return out;
}

/**
 * What to ask the server for, to fill a grid.
 *
 * The whole grid, not the month: the days either side are drawn too, and an event on the 31st of
 * August belongs in September's first row. The instants are the local start and end of those days,
 * because that is what the person is looking at.
 */
export function rangeOf(grid: Day[]): { from: string; to: string } {
    const firstDay = grid[0]?.date ?? new Date();
    const lastDay = grid[grid.length - 1]?.date ?? new Date();
    const from = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
    const to = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * The identity of one row on screen.
 *
 * Not the event's id: the occurrences of a repeat all live in ONE file and therefore all carry the
 * same id and the same uid. Two Mondays are two rows, and React needs to be able to tell them
 * apart — which is also the reason editing one of them is editing the series.
 */
export function keyOf(event: CalendarEvent): string {
    return `${event.id}#${event.start}`;
}

/** Every day an event touches, as day keys. */
export function daysOf(event: CalendarEvent): string[] {
    if (event.allDay) {
        /*
            The end of an all-day event is the day AFTER it, which is what iCalendar means by a
            non-inclusive end. Drawing that day too puts every one-day event across two squares —
            the mistake that makes a calendar look drunk.
        */
        const from = fromDayKey(event.start);
        const to = fromDayKey(event.end || event.start);
        const out: string[] = [];
        for (let d = new Date(from); d < to || out.length === 0; d.setDate(d.getDate() + 1)) {
            out.push(dayKey(d));
            if (out.length > 366) break;
        }
        return out;
    }

    const from = new Date(event.start);
    const rawEnd = event.end ? new Date(event.end) : from;
    if (Number.isNaN(from.getTime())) return [];
    /*
        An event that ends exactly at midnight ends on the previous day.

        18:00 → 00:00 is an evening, not two days, and a grid that draws it on both makes tomorrow
        look busy for something that finished before it started.
    */
    const end = new Date(Math.max(from.getTime(), rawEnd.getTime() - 1));
    const out: string[] = [];
    for (let d = new Date(from.getFullYear(), from.getMonth(), from.getDate()); d <= end; d.setDate(d.getDate() + 1)) {
        out.push(dayKey(d));
        if (out.length > 366) break;
    }
    return out;
}

/** The events of each day, in the order they happen. */
export function byDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
    const out = new Map<string, CalendarEvent[]>();
    for (const event of events) {
        for (const key of daysOf(event)) {
            const list = out.get(key);
            if (list) list.push(event);
            else out.set(key, [event]);
        }
    }
    for (const list of out.values()) {
        list.sort((a, b) => {
            // All-day events first: they are the day's heading, not something that happens at 00:00.
            if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
            return a.start < b.start ? -1 : a.start > b.start ? 1 : 0;
        });
    }
    return out;
}

/*
    The two directions a form has to convert in.

    `<input type="date">` and `<input type="time">` speak local wall clock — `2026-09-10` and
    `14:00` — and the server speaks instants. Both conversions live here so neither is written twice
    in a component and quietly differently the second time.
*/
export function toLocalFields(iso: string, allDay: boolean): { date: string; time: string } {
    if (allDay) return { date: iso.slice(0, 10), time: '' };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: '', time: '' };
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return { date: dayKey(d), time: `${hh}:${mm}` };
}

export function toInstant(date: string, time: string): string {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = (time || '00:00').split(':').map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0).toISOString();
}

/** The day after, for an all-day event's non-inclusive end. */
export function nextDay(key: string): string {
    const d = fromDayKey(key);
    d.setDate(d.getDate() + 1);
    return dayKey(d);
}
